import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Users, Building2, Briefcase, Bell, TrendingUp, 
  Search, Check, X, Crown, BarChart3, PieChart, Send,
  RefreshCw, Play, Pause, Clock, Database, Loader2,
  Archive, Trash2, Eye, EyeOff, Star, Gem, Settings2,
  Plus, Edit, Calendar, History, Layout, DollarSign,
  Filter, ToggleLeft, ToggleRight, Key, Download, CheckSquare
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { formatPublicationDate } from '@/components/JobCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchUser, setSearchUser] = useState('');
  const [searchCompany, setSearchCompany] = useState('');
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  
  // Users filter state
  const [usersFilter, setUsersFilter] = useState({
    status: 'all', // all, premium, free
    dateFrom: '',
    dateTo: '',
    limit: 50
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAllUsers, setSelectAllUsers] = useState(false);

  // Create user dialog state
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    nom: '',
    email: '',
    password: '',
    phone: '',
    profession: '',
    category_id: 'free'
  });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Scraping state
  const [scrapingStatus, setScrapingStatus] = useState(null);
  const [scrapingLoading, setScrapingLoading] = useState(false);
  const [lastScrapingResult, setLastScrapingResult] = useState(null);
  
  // Facebook state
  const [facebookStatus, setFacebookStatus] = useState(null);
  const [facebookTesting, setFacebookTesting] = useState(false);
  const [facebookToken, setFacebookToken] = useState(null);
  const [facebookTokenChecking, setFacebookTokenChecking] = useState(false);
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    name_fr: '',
    cost: 0,
    validity_days: 30,
    color: '#F59E0B'
  });
  
  // Jobs management state
  const [jobs, setJobs] = useState([]);
  const [jobsStats, setJobsStats] = useState(null);
  const [searchJob, setSearchJob] = useState('');
  const [showArchivedJobs, setShowArchivedJobs] = useState(true);
  const [jobsFilter, setJobsFilter] = useState({
    status: 'all', // all, active, archived
    dateFrom: '',
    dateTo: '',
    limit: 50
  });
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectAllJobs, setSelectAllJobs] = useState(false);
  
  // Activations history state
  const [activations, setActivations] = useState([]);
  const [activationsStats, setActivationsStats] = useState(null);
  const [activationsFilter, setActivationsFilter] = useState({
    category_id: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    limit: 50
  });
  const [searchActivation, setSearchActivation] = useState('');
  
  // Sections configuration state
  const [sections, setSections] = useState({});
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Auto-archive scheduler state
  const [archiveSchedulerStatus, setArchiveSchedulerStatus] = useState(null);
  
  const [pushForm, setPushForm] = useState({
    title: '',
    message: '',
    target: 'all'
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/auth');
      return;
    }
    fetchAllData();
    fetchScrapingStatus();
    fetchCategories();
    fetchJobs();
    fetchJobsStats();
    fetchActivations();
    fetchSections();
    fetchArchiveSchedulerStatus();
    fetchFacebookStatus();
    checkFacebookToken();
  }, []);

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`
  });

  const fetchAllData = async () => {
    try {
      const headers = getHeaders();
      const [statsRes, usersRes, companiesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users/detailed?limit=100`, { headers }),
        axios.get(`${API}/admin/companies?limit=100`, { headers }),
        axios.get(`${API}/admin/analytics`, { headers })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setCompanies(companiesRes.data.companies);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      if (error.response?.status === 401) {
        navigate('/admin/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  // Categories management
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`, { headers: getHeaders() });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(
          `${API}/admin/categories/${editingCategory.id}`, 
          categoryForm,
          { headers: getHeaders() }
        );
        toast.success('Catégorie mise à jour');
      } else {
        await axios.post(`${API}/admin/categories`, categoryForm, { headers: getHeaders() });
        toast.success('Catégorie créée');
      }
      fetchCategories();
      setCategoryDialogOpen(false);
      resetCategoryForm();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie?')) return;
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, { headers: getHeaders() });
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      name_fr: category.name_fr,
      cost: category.cost,
      validity_days: category.validity_days,
      color: category.color
    });
    setCategoryDialogOpen(true);
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      name_fr: '',
      cost: 0,
      validity_days: 30,
      color: '#F59E0B'
    });
  };

  // Jobs management
  const fetchJobs = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/jobs?include_archived=${showArchivedJobs}`, 
        { headers: getHeaders() }
      );
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchJobsStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/jobs/stats`, { headers: getHeaders() });
      setJobsStats(response.data);
    } catch (error) {
      console.error('Error fetching jobs stats:', error);
    }
  };

  const fetchArchiveSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/archive-scheduler/status`, { headers: getHeaders() });
      setArchiveSchedulerStatus(response.data);
    } catch (error) {
      console.error('Error fetching archive scheduler status:', error);
    }
  };

  const toggleArchiveScheduler = async (enable) => {
    try {
      const endpoint = enable ? 'start' : 'stop';
      await axios.post(`${API}/admin/archive-scheduler/${endpoint}`, {}, { headers: getHeaders() });
      toast.success(enable ? 'Auto-archivage activé' : 'Auto-archivage désactivé');
      fetchArchiveSchedulerStatus();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleArchiveJob = async (jobId) => {
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/archive`, {}, { headers: getHeaders() });
      toast.success('Emploi archivé');
      fetchJobs();
      fetchJobsStats();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleUnarchiveJob = async (jobId) => {
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/unarchive`, {}, { headers: getHeaders() });
      toast.success('Emploi restauré');
      fetchJobs();
      fetchJobsStats();
    } catch (error) {
      toast.error('Erreur lors de la restauration');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement cet emploi?')) return;
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`, { headers: getHeaders() });
      toast.success('Emploi supprimé');
      fetchJobs();
      fetchJobsStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Bulk delete selected jobs
  const handleBulkDeleteJobs = async () => {
    if (selectedJobs.length === 0) {
      toast.error('Aucun emploi sélectionné');
      return;
    }
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedJobs.length} emploi(s) définitivement?`)) return;
    
    try {
      let deleted = 0;
      for (const jobId of selectedJobs) {
        await axios.delete(`${API}/admin/jobs/${jobId}`, { headers: getHeaders() });
        deleted++;
      }
      toast.success(`${deleted} emploi(s) supprimé(s)`);
      setSelectedJobs([]);
      setSelectAllJobs(false);
      fetchJobs();
      fetchJobsStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Bulk archive selected jobs
  const handleBulkArchiveJobs = async () => {
    if (selectedJobs.length === 0) {
      toast.error('Aucun emploi sélectionné');
      return;
    }
    
    try {
      let archived = 0;
      for (const jobId of selectedJobs) {
        await axios.put(`${API}/admin/jobs/${jobId}/archive`, {}, { headers: getHeaders() });
        archived++;
      }
      toast.success(`${archived} emploi(s) archivé(s)`);
      setSelectedJobs([]);
      setSelectAllJobs(false);
      fetchJobs();
      fetchJobsStats();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  // Export jobs to CSV
  const exportJobsToCSV = () => {
    // Get filtered jobs
    const filteredJobs = jobs.filter(job => {
      const matchesSearch = 
        job.titre?.toLowerCase().includes(searchJob.toLowerCase()) ||
        job.entreprise?.toLowerCase().includes(searchJob.toLowerCase());
      
      const matchesStatus = 
        jobsFilter.status === 'all' ||
        (jobsFilter.status === 'active' && !job.archived) ||
        (jobsFilter.status === 'archived' && job.archived);
      
      let matchesDate = true;
      if (job.date_publication) {
        const jobDate = new Date(job.date_publication);
        if (jobsFilter.dateFrom) {
          matchesDate = matchesDate && jobDate >= new Date(jobsFilter.dateFrom);
        }
        if (jobsFilter.dateTo) {
          matchesDate = matchesDate && jobDate <= new Date(jobsFilter.dateTo + 'T23:59:59');
        }
      }
      
      return matchesSearch && matchesStatus && matchesDate;
    }).slice(0, jobsFilter.limit);

    // Create CSV content
    const headers = ['Titre', 'Entreprise', 'Localisation', 'Source', 'Statut', 'Date Publication', 'Lien'];
    const rows = filteredJobs.map(job => [
      `"${(job.titre || '').replace(/"/g, '""')}"`,
      `"${(job.entreprise || '').replace(/"/g, '""')}"`,
      `"${(job.localisation || '').replace(/"/g, '""')}"`,
      `"${(job.source || '').replace(/"/g, '""')}"`,
      job.archived ? 'Archivé' : 'Actif',
      job.date_publication ? new Date(job.date_publication).toLocaleDateString('fr-FR') : '',
      `"${(job.source_link || job.lien || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    
    // Download file
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `mosala_emplois_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success(`${filteredJobs.length} emploi(s) exporté(s) en CSV`);
  };

  // Export users to CSV
  const exportUsersToCSV = () => {
    const filteredUsers = users.filter(user => {
      const matchesSearch = 
        user.nom?.toLowerCase().includes(searchUser.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchUser.toLowerCase());
      
      const matchesStatus = 
        usersFilter.status === 'all' ||
        (usersFilter.status === 'premium' && user.is_premium) ||
        (usersFilter.status === 'free' && !user.is_premium);
      
      let matchesDate = true;
      if (user.created_at) {
        const userDate = new Date(user.created_at);
        if (usersFilter.dateFrom) {
          matchesDate = matchesDate && userDate >= new Date(usersFilter.dateFrom);
        }
        if (usersFilter.dateTo) {
          matchesDate = matchesDate && userDate <= new Date(usersFilter.dateTo + 'T23:59:59');
        }
      }
      
      return matchesSearch && matchesStatus && matchesDate;
    }).slice(0, usersFilter.limit);

    const headers = ['Nom', 'Email', 'Téléphone', 'Profession', 'Catégorie', 'Premium', 'Date Inscription'];
    const rows = filteredUsers.map(user => [
      `"${(user.nom || '').replace(/"/g, '""')}"`,
      `"${(user.email || '').replace(/"/g, '""')}"`,
      `"${(user.phone || '').replace(/"/g, '""')}"`,
      `"${(user.profession || '').replace(/"/g, '""')}"`,
      user.category_id || 'free',
      user.is_premium ? 'Oui' : 'Non',
      user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : ''
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `mosala_utilisateurs_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success(`${filteredUsers.length} utilisateur(s) exporté(s) en CSV`);
  };

  // Export activations to CSV
  const exportActivationsToCSV = () => {
    const filteredActivations = activations.filter(act => {
      const matchesSearch = 
        act.user_email?.toLowerCase().includes(searchActivation.toLowerCase()) ||
        act.user_name?.toLowerCase().includes(searchActivation.toLowerCase()) ||
        act.transaction_id?.toLowerCase().includes(searchActivation.toLowerCase());
      
      const matchesCategory = !activationsFilter.category_id || act.category_id === activationsFilter.category_id;
      const matchesStatus = !activationsFilter.status || act.status === activationsFilter.status;
      
      let matchesDate = true;
      if (act.created_at) {
        const actDate = new Date(act.created_at);
        if (activationsFilter.dateFrom) {
          matchesDate = matchesDate && actDate >= new Date(activationsFilter.dateFrom);
        }
        if (activationsFilter.dateTo) {
          matchesDate = matchesDate && actDate <= new Date(activationsFilter.dateTo + 'T23:59:59');
        }
      }
      
      return matchesSearch && matchesCategory && matchesStatus && matchesDate;
    }).slice(0, activationsFilter.limit);

    const headers = ['Transaction', 'Utilisateur', 'Email', 'Catégorie', 'Montant', 'Téléphone', 'Statut', 'Date'];
    const rows = filteredActivations.map(act => [
      `"${(act.transaction_id || '').replace(/"/g, '""')}"`,
      `"${(act.user_name || '').replace(/"/g, '""')}"`,
      `"${(act.user_email || '').replace(/"/g, '""')}"`,
      act.category_id || '',
      `${act.amount || 0} USD`,
      `"${(act.phone || '').replace(/"/g, '""')}"`,
      act.status || '',
      act.created_at ? new Date(act.created_at).toLocaleDateString('fr-FR') : ''
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `mosala_activations_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success(`${filteredActivations.length} activation(s) exportée(s) en CSV`);
  };

  // Handle user selection
  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Handle select all users
  const handleSelectAllUsers = (filteredUsers) => {
    if (selectAllUsers) {
      setSelectedUsers([]);
      setSelectAllUsers(false);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
      setSelectAllUsers(true);
    }
  };

  // Handle job selection
  const handleJobSelect = (jobId) => {
    setSelectedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  // Handle select all jobs (visible ones)
  const handleSelectAllJobs = (filteredJobs) => {
    if (selectAllJobs) {
      setSelectedJobs([]);
      setSelectAllJobs(false);
    } else {
      setSelectedJobs(filteredJobs.map(job => job.id));
      setSelectAllJobs(true);
    }
  };

  // Category icon helper
  const getCategoryIcon = (categoryId) => {
    switch (categoryId) {
      case 'diamond': return <Gem className="h-4 w-4" />;
      case 'gold': return <Star className="h-4 w-4" />;
      case 'premium': return <Crown className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  // Activations history
  const fetchActivations = async () => {
    try {
      const params = new URLSearchParams();
      if (activationsFilter.category_id) params.append('category_id', activationsFilter.category_id);
      if (activationsFilter.status) params.append('status', activationsFilter.status);
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/admin/activations?${params}`, { headers: getHeaders() });
      setActivations(response.data.activations || []);
      setActivationsStats(response.data.stats || null);
    } catch (error) {
      console.error('Error fetching activations:', error);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('admin_token')) {
      fetchActivations();
    }
  }, [activationsFilter]);

  // Sections configuration
  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API}/admin/sections`, { headers: getHeaders() });
      setSections(response.data.sections || {});
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const handleToggleSectionVisibility = async (sectionId, currentVisible) => {
    try {
      await axios.put(
        `${API}/admin/sections/${sectionId}`,
        { visible: !currentVisible },
        { headers: getHeaders() }
      );
      toast.success(`Section ${!currentVisible ? 'activée' : 'désactivée'}`);
      fetchSections();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleUpdateSectionContent = async (sectionId, content) => {
    try {
      await axios.put(
        `${API}/admin/sections/${sectionId}`,
        { content },
        { headers: getHeaders() }
      );
      toast.success('Section mise à jour');
      fetchSections();
      setSectionDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const fetchScrapingStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/scraping/status`, { headers: getHeaders() });
      setScrapingStatus(response.data);
    } catch (error) {
      console.error('Error fetching scraping status:', error);
    }
  };

  const runScraping = async () => {
    setScrapingLoading(true);
    try {
      const response = await axios.post(`${API}/admin/scraping/run`, {}, { headers: getHeaders() });
      setLastScrapingResult(response.data);
      toast.success(`Scraping terminé: ${response.data.jobs_added} emplois ajoutés`);
      fetchAllData();
      fetchScrapingStatus();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('Un scraping est déjà en cours');
      } else {
        toast.error('Erreur lors du scraping');
      }
    } finally {
      setScrapingLoading(false);
    }
  };

  const toggleScheduler = async (start) => {
    try {
      const interval = scrapingStatus?.settings?.scheduler_interval_hours || 6;
      if (start) {
        await axios.post(`${API}/admin/scraping/scheduler/start?interval_hours=${interval}`, {}, { headers: getHeaders() });
        toast.success(`Scheduler démarré (intervalle: ${interval}h)`);
      } else {
        await axios.post(`${API}/admin/scraping/scheduler/stop`, {}, { headers: getHeaders() });
        toast.success('Scheduler arrêté');
      }
      fetchScrapingStatus();
    } catch (error) {
      toast.error('Erreur lors du changement du scheduler');
    }
  };

  const updateScrapingSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/admin/scraping/settings`, newSettings, { headers: getHeaders() });
      toast.success('Paramètres de scraping mis à jour');
      fetchScrapingStatus();
      fetchFacebookStatus();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des paramètres');
    }
  };

  const fetchFacebookStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/facebook/status`, { headers: getHeaders() });
      setFacebookStatus(response.data);
    } catch (error) {
      console.error('Error fetching facebook status:', error);
    }
  };

  const testFacebookConnection = async () => {
    setFacebookTesting(true);
    try {
      const response = await axios.post(`${API}/admin/facebook/test`, {}, { headers: getHeaders() });
      if (response.data?.success) {
        toast.success('Post de test publié sur Facebook avec succès');
      } else {
        toast.error(`Échec: ${response.data?.error || 'erreur inconnue'}`);
      }
      fetchFacebookStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du test Facebook');
    } finally {
      setFacebookTesting(false);
    }
  };

  const checkFacebookToken = async () => {
    setFacebookTokenChecking(true);
    try {
      const response = await axios.get(`${API}/admin/facebook/debug-token`, { headers: getHeaders() });
      setFacebookToken(response.data);
    } catch (error) {
      console.error('Error checking facebook token:', error);
    } finally {
      setFacebookTokenChecking(false);
    }
  };

  const toggleUserPremium = async (userId, currentStatus) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/premium?enable=${!currentStatus}`,
        {},
        { headers: getHeaders() }
      );
      toast.success('Statut premium mis à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleUserActive = async (userId, currentActive) => {
    const next = !currentActive;
    if (!next && !window.confirm("Désactiver ce compte ? L'utilisateur ne pourra plus se connecter.")) return;
    try {
      await axios.put(
        `${API}/admin/users/${userId}/active`,
        { is_active: next },
        { headers: getHeaders() }
      );
      toast.success(next ? 'Compte activé' : 'Compte désactivé');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleCreateUser = async () => {
    if (!newUserForm.nom || !newUserForm.email || !newUserForm.password) {
      toast.error('Nom, email et mot de passe sont requis');
      return;
    }
    if (newUserForm.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setCreatingUser(true);
    try {
      await axios.post(
        `${API}/admin/users`,
        newUserForm,
        { headers: getHeaders() }
      );
      toast.success(`Utilisateur ${newUserForm.email} créé avec succès`);
      setCreateUserDialogOpen(false);
      setNewUserForm({ nom: '', email: '', password: '', phone: '', profession: '', category_id: 'free' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingUser(false);
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUserForPassword(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleChangeUserPassword = async () => {
    if (!selectedUserForPassword || !newPassword) {
      toast.error('Veuillez saisir un nouveau mot de passe');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.put(
        `${API}/admin/users/${selectedUserForPassword.id}/password`,
        { user_id: selectedUserForPassword.id, new_password: newPassword },
        { headers: getHeaders() }
      );
      toast.success(`Mot de passe de ${selectedUserForPassword.email} mis à jour avec succès`);
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUserForPassword(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour du mot de passe');
    } finally {
      setPasswordLoading(false);
    }
  };

  const toggleCompanyVerification = async (companyId, currentStatus) => {
    try {
      await axios.put(
        `${API}/admin/companies/${companyId}/verify?verify=${!currentStatus}`,
        {},
        { headers: getHeaders() }
      );
      toast.success('Vérification mise à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const sendPushNotification = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/admin/push-notification`,
        pushForm,
        { headers: getHeaders() }
      );
      toast.success(`${response.data.count} notifications envoyées!`);
      setPushDialogOpen(false);
      setPushForm({ title: '', message: '', target: 'all' });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/');
  };

  const filteredUsers = users.filter(user =>
    user.nom.toLowerCase().includes(searchUser.toLowerCase()) ||
    user.email.toLowerCase().includes(searchUser.toLowerCase())
  );

  const filteredCompanies = companies.filter(company =>
    company.nom.toLowerCase().includes(searchCompany.toLowerCase()) ||
    company.email.toLowerCase().includes(searchCompany.toLowerCase())
  );

  if (loading && !stats) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white border-b border-slate-700 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Administration Mosala.cd</h1>
                <p className="text-sm text-slate-300">Dashboard de gestion</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout} className="text-white border-white hover:bg-slate-700">
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-100 mb-1">Utilisateurs</p>
                <p className="text-3xl font-bold">{stats?.users?.total || 0}</p>
                <p className="text-xs text-blue-100 mt-1">{stats?.users?.premium || 0} Premium</p>
              </div>
              <Users className="h-12 w-12 opacity-80" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-100 mb-1">Entreprises</p>
                <p className="text-3xl font-bold">{stats?.companies?.total || 0}</p>
                <p className="text-xs text-purple-100 mt-1">{stats?.companies?.verified || 0} Vérifiées</p>
              </div>
              <Building2 className="h-12 w-12 opacity-80" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-100 mb-1">Offres d'emploi</p>
                <p className="text-3xl font-bold">{stats?.jobs?.total || 0}</p>
                <p className="text-xs text-green-100 mt-1">{stats?.jobs?.from_companies || 0} Entreprises</p>
              </div>
              <Briefcase className="h-12 w-12 opacity-80" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-100 mb-1">Taux Premium</p>
                <p className="text-3xl font-bold">{stats?.users?.premium_rate || 0}%</p>
                <p className="text-xs text-yellow-100 mt-1">Revenue: {(stats?.users?.premium || 0) * 1} USD</p>
              </div>
              <TrendingUp className="h-12 w-12 opacity-80" />
            </div>
          </Card>
        </div>

        {/* Main Tabs */}
        <Card className="p-6">
          <Tabs defaultValue="users" className="w-full">
            <TabsList className="grid w-full grid-cols-9 mb-6">
              <TabsTrigger value="users" data-testid="users-tab">
                <Users className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Utilisateurs</span>
              </TabsTrigger>
              <TabsTrigger value="categories" data-testid="categories-tab">
                <Settings2 className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Catégories</span>
              </TabsTrigger>
              <TabsTrigger value="activations" data-testid="activations-tab">
                <History className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Activations</span>
              </TabsTrigger>
              <TabsTrigger value="jobs" data-testid="jobs-tab">
                <Briefcase className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Emplois</span>
              </TabsTrigger>
              <TabsTrigger value="sections" data-testid="sections-tab">
                <Layout className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Sections</span>
              </TabsTrigger>
              <TabsTrigger value="companies">
                <Building2 className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Entreprises</span>
              </TabsTrigger>
              <TabsTrigger value="scraping">
                <Database className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Scraping</span>
              </TabsTrigger>
              <TabsTrigger value="analytics">
                <BarChart3 className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Analytics</span>
              </TabsTrigger>
              <TabsTrigger value="push">
                <Send className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Push</span>
              </TabsTrigger>
            </TabsList>

            {/* Activations History Tab */}
            <TabsContent value="activations" className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Historique des Activations</h2>
                  {activationsStats && (
                    <p className="text-sm text-slate-500 mt-1">
                      {activationsStats.completed} complétées | {activationsStats.pending} en attente | 
                      <span className="text-green-600 font-semibold ml-1">
                        ${activationsStats.total_revenue} revenus
                      </span>
                    </p>
                  )}
                </div>
              </div>

              {/* Filtres Activations */}
              <div className="bg-slate-50 border rounded-lg p-4">
                <div className="flex flex-wrap items-end gap-4">
                  {/* Recherche */}
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs text-slate-500 mb-1 block">Recherche</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Nom, email ou transaction..."
                        value={searchActivation}
                        onChange={(e) => setSearchActivation(e.target.value)}
                        className="pl-10"
                        data-testid="search-activations-input"
                      />
                    </div>
                  </div>
                  
                  {/* Filtre Catégorie */}
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">Catégorie</Label>
                    <Select
                      value={activationsFilter.category_id || 'all'}
                      onValueChange={(v) => setActivationsFilter(prev => ({...prev, category_id: v === 'all' ? '' : v}))}
                    >
                      <SelectTrigger data-testid="filter-activations-category">
                        <SelectValue placeholder="Toutes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                        <SelectItem value="gold">Gold</SelectItem>
                        <SelectItem value="diamond">Diamond</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Filtre Statut */}
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">Statut</Label>
                    <Select
                      value={activationsFilter.status || 'all'}
                      onValueChange={(v) => setActivationsFilter(prev => ({...prev, status: v === 'all' ? '' : v}))}
                    >
                      <SelectTrigger data-testid="filter-activations-status">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="completed">Complété</SelectItem>
                        <SelectItem value="pending">En attente</SelectItem>
                        <SelectItem value="failed">Échoué</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Date Du */}
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">Date du</Label>
                    <Input
                      type="date"
                      value={activationsFilter.dateFrom}
                      onChange={(e) => setActivationsFilter({...activationsFilter, dateFrom: e.target.value})}
                      data-testid="filter-activations-date-from"
                    />
                  </div>
                  
                  {/* Date Au */}
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">Date au</Label>
                    <Input
                      type="date"
                      value={activationsFilter.dateTo}
                      onChange={(e) => setActivationsFilter({...activationsFilter, dateTo: e.target.value})}
                      data-testid="filter-activations-date-to"
                    />
                  </div>
                  
                  {/* Nombre */}
                  <div className="w-32">
                    <Label className="text-xs text-slate-500 mb-1 block">Afficher</Label>
                    <Select 
                      value={String(activationsFilter.limit)}
                      onValueChange={(value) => setActivationsFilter({...activationsFilter, limit: parseInt(value)})}
                    >
                      <SelectTrigger data-testid="filter-activations-limit">
                        <SelectValue placeholder="50" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="500">Tous</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Bouton Reset */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setActivationsFilter({ category_id: '', status: '', dateFrom: '', dateTo: '', limit: 50 });
                      setSearchActivation('');
                    }}
                    data-testid="reset-activations-filters"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Réinitialiser
                  </Button>
                  
                  {/* Bouton Export CSV */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportActivationsToCSV}
                    data-testid="export-activations-csv"
                    className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Exporter CSV
                  </Button>
                  
                  {/* Bouton Refresh */}
                  <Button onClick={fetchActivations} variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Stats Cards */}
              {activationsStats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Complétées</p>
                        <p className="text-2xl font-bold">{activationsStats.completed}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <Clock className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">En attente</p>
                        <p className="text-2xl font-bold">{activationsStats.pending}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-sky-100 rounded-lg">
                        <DollarSign className="h-5 w-5 text-sky-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Revenus totaux</p>
                        <p className="text-2xl font-bold">${activationsStats.total_revenue}</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total</p>
                        <p className="text-2xl font-bold">{activationsStats.completed + activationsStats.pending}</p>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* Activations Table */}
              <div className="border rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Utilisateur</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Catégorie</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Montant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Téléphone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Statut</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Transaction</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {activations
                      .filter(act => {
                        const matchesSearch = 
                          act.user_email?.toLowerCase().includes(searchActivation.toLowerCase()) ||
                          act.user_name?.toLowerCase().includes(searchActivation.toLowerCase()) ||
                          act.transaction_id?.toLowerCase().includes(searchActivation.toLowerCase());
                        
                        const matchesCategory = !activationsFilter.category_id || act.category_id === activationsFilter.category_id;
                        const matchesStatus = !activationsFilter.status || act.status === activationsFilter.status;
                        
                        let matchesDate = true;
                        if (act.created_at) {
                          const actDate = new Date(act.created_at);
                          if (activationsFilter.dateFrom) {
                            matchesDate = matchesDate && actDate >= new Date(activationsFilter.dateFrom);
                          }
                          if (activationsFilter.dateTo) {
                            matchesDate = matchesDate && actDate <= new Date(activationsFilter.dateTo + 'T23:59:59');
                          }
                        }
                        
                        return matchesSearch && matchesCategory && matchesStatus && matchesDate;
                      })
                      .slice(0, activationsFilter.limit)
                      .map((activation) => (
                      <tr key={activation.transaction_id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-sm">{activation.user_name}</p>
                            <p className="text-xs text-slate-500">{activation.user_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            activation.category_id === 'diamond' ? 'bg-cyan-100 text-cyan-700' :
                            activation.category_id === 'gold' ? 'bg-yellow-100 text-yellow-700' :
                            activation.category_id === 'premium' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {getCategoryIcon(activation.category_id)}
                            <span className="ml-1">{activation.category_name}</span>
                          </Badge>
                        </td>
                        <td className="px-4 py-3 font-semibold text-green-600">
                          ${activation.amount} {activation.currency}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {activation.phone}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            activation.status === 'completed' ? 'bg-green-100 text-green-700' :
                            activation.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {activation.status === 'completed' ? 'Complété' :
                             activation.status === 'pending' ? 'En attente' : 'Échoué'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {new Date(activation.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-slate-400">
                          {activation.transaction_id?.slice(0, 15)}...
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {activations.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <History className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucune activation trouvée</p>
                </div>
              )}
            </TabsContent>

            {/* Sections Configuration Tab */}
            <TabsContent value="sections" className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Configuration des Sections</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Gérez la visibilité et le contenu des sections de la page d'accueil
                  </p>
                </div>
                <Button onClick={fetchSections} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Actualiser
                </Button>
              </div>

              <div className="space-y-4">
                {Object.values(sections).sort((a, b) => (a.order || 0) - (b.order || 0)).map((section) => (
                  <Card key={section.id} className={`p-4 ${!section.visible ? 'opacity-60 bg-slate-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${section.visible ? 'bg-green-100' : 'bg-slate-200'}`}>
                          <Layout className={`h-5 w-5 ${section.visible ? 'text-green-600' : 'text-slate-500'}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold">{section.name}</h3>
                          <p className="text-sm text-slate-500">ID: {section.id}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">
                            {section.visible ? 'Visible' : 'Masqué'}
                          </span>
                          <Switch
                            checked={section.visible}
                            onCheckedChange={() => handleToggleSectionVisibility(section.id, section.visible)}
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingSection(section);
                            setSectionDialogOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Éditer
                        </Button>
                      </div>
                    </div>
                    
                    {section.content && (
                      <div className="mt-3 pl-14">
                        {section.content.title && (
                          <p className="text-sm"><span className="text-slate-500">Titre:</span> {section.content.title}</p>
                        )}
                        {section.content.subtitle && (
                          <p className="text-sm text-slate-500 truncate max-w-xl">{section.content.subtitle}</p>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Les modifications sont appliquées immédiatement sur la page d'accueil.
                  La section "Employeurs" est désactivée par défaut pour se concentrer d'abord sur les chercheurs d'emploi.
                </p>
              </div>
            </TabsContent>

            {/* Scraping Tab */}
            <TabsContent value="scraping" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Gestion du Scraping Automatique</h2>
                <Button onClick={fetchScrapingStatus} variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Actualiser
                </Button>
              </div>

              {/* Scraping Status Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Statut du Service</h3>
                    {scrapingStatus?.is_running ? (
                      <Badge className="bg-green-100 text-green-700">
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        En cours
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-700">Inactif</Badge>
                    )}
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="flex justify-between">
                      <span className="text-slate-500">Mode:</span>
                      <span className="font-medium">{scrapingStatus?.demo_mode ? '🎭 Démo' : '🔌 Production'}</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-slate-500">Dernière exécution:</span>
                      <span className="font-medium">
                        {scrapingStatus?.last_run 
                          ? new Date(scrapingStatus.last_run).toLocaleString('fr-FR')
                          : 'Jamais'
                        }
                      </span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-slate-500">Emplois ajoutés total:</span>
                      <span className="font-medium">{scrapingStatus?.stats?.total_jobs_added || 0}</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-slate-500">Notifications envoyées:</span>
                      <span className="font-medium">{scrapingStatus?.stats?.total_notifications_sent || 0}</span>
                    </p>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Scheduler Automatique</h3>
                    {scrapingStatus?.scheduler?.is_started ? (
                      <Badge className="bg-green-100 text-green-700">Actif</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-700">Arrêté</Badge>
                    )}
                  </div>
                  <div className="space-y-2 text-sm mb-4">
                    <p className="flex justify-between">
                      <span className="text-slate-500">Intervalle:</span>
                      <span className="font-medium">{scrapingStatus?.scheduler?.interval_hours || 6}h</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-slate-500">Prochaine exécution:</span>
                      <span className="font-medium">
                        {scrapingStatus?.scheduler?.next_run 
                          ? new Date(scrapingStatus.scheduler.next_run).toLocaleString('fr-FR')
                          : 'Non planifiée'
                        }
                      </span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {scrapingStatus?.scheduler?.is_started ? (
                      <Button onClick={() => toggleScheduler(false)} variant="outline" className="w-full">
                        <Pause className="h-4 w-4 mr-2" />
                        Arrêter
                      </Button>
                    ) : (
                      <Button onClick={() => toggleScheduler(true)} className="w-full bg-green-600 hover:bg-green-700">
                        <Play className="h-4 w-4 mr-2" />
                        Démarrer
                      </Button>
                    )}
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Exécution Manuelle</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    Lancez un cycle de scraping immédiatement pour ajouter de nouveaux emplois et envoyer des notifications.
                  </p>
                  <Button 
                    onClick={runScraping} 
                    disabled={scrapingLoading || scrapingStatus?.is_running}
                    className="w-full bg-sky-600 hover:bg-sky-700"
                  >
                    {scrapingLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Scraping en cours...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Lancer le Scraping
                      </>
                    )}
                  </Button>
                </Card>
              </div>

              {/* Last Scraping Result */}
              {lastScrapingResult && (
                <Card className="p-6 bg-slate-50">
                  <h3 className="font-semibold mb-4">Dernier Résultat</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-slate-500">Statut</p>
                      <p className="font-bold text-lg">
                        {lastScrapingResult.success ? '✅ Succès' : '❌ Échec'}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-slate-500">Emplois scrapés</p>
                      <p className="font-bold text-lg">{lastScrapingResult.jobs_scraped}</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-slate-500">Emplois ajoutés</p>
                      <p className="font-bold text-lg text-green-600">{lastScrapingResult.jobs_added}</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-slate-500">Notifications</p>
                      <p className="font-bold text-lg text-blue-600">
                        {(lastScrapingResult.notifications?.notifications_web || 0) + 
                         (lastScrapingResult.notifications?.notifications_sms || 0)}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-slate-500">Durée</p>
                      <p className="font-bold text-lg">{lastScrapingResult.duration_seconds?.toFixed(1)}s</p>
                    </div>
                  </div>
                  {lastScrapingResult.error && (
                    <p className="text-red-600 mt-4">Erreur: {lastScrapingResult.error}</p>
                  )}
                </Card>
              )}

              {/* Scraping Settings */}
              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  ⚙️ Paramètres de Scraping
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600">Mot-clé de recherche</Label>
                    <Input
                      value={scrapingStatus?.settings?.search_query || 'emploi RDC'}
                      onChange={(e) => {
                        const newQuery = e.target.value;
                        if (newQuery.length >= 3) {
                          updateScrapingSettings({ search_query: newQuery });
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value.length >= 3) {
                          updateScrapingSettings({ search_query: e.target.value });
                        }
                      }}
                      placeholder="emploi RDC"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Localisation</Label>
                    <Input
                      value={scrapingStatus?.settings?.location || 'Democratic Republic of the Congo'}
                      onChange={(e) => {
                        const newLocation = e.target.value;
                        if (newLocation.length >= 3) {
                          updateScrapingSettings({ location: newLocation });
                        }
                      }}
                      onBlur={(e) => {
                        if (e.target.value.length >= 3) {
                          updateScrapingSettings({ location: e.target.value });
                        }
                      }}
                      placeholder="Democratic Republic of the Congo"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Nombre d'emplois max</Label>
                    <Select 
                      value={String(scrapingStatus?.settings?.num_results || 100)}
                      onValueChange={(value) => updateScrapingSettings({ num_results: parseInt(value) })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="100" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20 emplois</SelectItem>
                        <SelectItem value="50">50 emplois</SelectItem>
                        <SelectItem value="100">100 emplois (max)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Filtre Date Publication</Label>
                    <Select 
                      value={scrapingStatus?.settings?.date_posted || 'today'}
                      onValueChange={(value) => updateScrapingSettings({ date_posted: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Dernières 24h" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="today">Dernières 24h (Yesterday)</SelectItem>
                        <SelectItem value="3days">3 derniers jours</SelectItem>
                        <SelectItem value="week">Dernière semaine</SelectItem>
                        <SelectItem value="month">Dernier mois</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Intervalle scheduler</Label>
                    <Select 
                      value={String(scrapingStatus?.settings?.scheduler_interval_hours || 6)}
                      onValueChange={(value) => updateScrapingSettings({ scheduler_interval_hours: parseInt(value) })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="6h" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Toutes les 1h</SelectItem>
                        <SelectItem value="3">Toutes les 3h</SelectItem>
                        <SelectItem value="6">Toutes les 6h</SelectItem>
                        <SelectItem value="12">Toutes les 12h</SelectItem>
                        <SelectItem value="24">Toutes les 24h</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  💡 Les paramètres sont sauvegardés automatiquement. Le mot-clé par défaut est "emploi RDC" et la limite maximale est de 100 emplois par scraping.
                </p>
              </Card>

              {/* Facebook Auto-Posting Card */}
              <Card className="p-6" data-testid="facebook-config-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold">f</div>
                    <div>
                      <h3 className="text-lg font-semibold">Publication automatique Facebook</h3>
                      <p className="text-xs text-slate-500">
                        Publie les nouveaux emplois sur la Page Facebook après chaque cycle de scraping
                      </p>
                    </div>
                  </div>
                  {facebookStatus?.configured ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200">Connectée</Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700 border-red-200">Non configurée</Badge>
                  )}
                </div>

                {!facebookStatus?.configured && (
                  <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800">
                    ⚠️ Pour activer la publication, ajoutez <code className="bg-amber-100 px-1 rounded">FACEBOOK_PAGE_ID</code> et <code className="bg-amber-100 px-1 rounded">FACEBOOK_PAGE_ACCESS_TOKEN</code> dans <code className="bg-amber-100 px-1 rounded">backend/.env</code>, puis redémarrez le backend.
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Toggle */}
                  <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                    <div>
                      <Label htmlFor="fb-auto-toggle" className="font-medium">Auto-publication activée</Label>
                      <p className="text-xs text-slate-500 mt-1">
                        Publie automatiquement après chaque cycle de scraping
                      </p>
                    </div>
                    <Switch
                      id="fb-auto-toggle"
                      data-testid="fb-auto-post-toggle"
                      checked={!!scrapingStatus?.settings?.facebook_auto_post_enabled}
                      disabled={!facebookStatus?.configured}
                      onCheckedChange={(checked) => updateScrapingSettings({ facebook_auto_post_enabled: checked })}
                    />
                  </div>

                  {/* Mode Select */}
                  <div className="p-4 rounded-lg border border-slate-200">
                    <Label className="font-medium">Mode de publication</Label>
                    <p className="text-xs text-slate-500 mt-1 mb-2">
                      Récap = un seul post résumé. Individuel = un post par emploi (max 3)
                    </p>
                    <Select
                      value={scrapingStatus?.settings?.facebook_post_mode || 'summary'}
                      onValueChange={(value) => updateScrapingSettings({ facebook_post_mode: value })}
                    >
                      <SelectTrigger data-testid="fb-post-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="summary">📋 Récapitulatif (1 post)</SelectItem>
                        <SelectItem value="individual">📝 Individuel (1 post / emploi)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Page ID + Test */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500">Page Facebook ID</Label>
                    <div className="mt-1 p-2 rounded bg-slate-50 border border-slate-200 font-mono text-sm" data-testid="fb-page-id">
                      {facebookStatus?.page_id || '—'}
                    </div>
                  </div>
                  <Button
                    onClick={testFacebookConnection}
                    disabled={!facebookStatus?.configured || facebookTesting}
                    variant="outline"
                    data-testid="fb-test-button"
                  >
                    {facebookTesting ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi...</>
                    ) : (
                      <><Send className="h-4 w-4 mr-2" /> Publier un test</>
                    )}
                  </Button>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 rounded-md bg-slate-50">
                    <p className="text-slate-500 text-xs">Total publications</p>
                    <p className="font-semibold text-lg" data-testid="fb-total-posts">{facebookStatus?.total_posts ?? 0}</p>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50">
                    <p className="text-slate-500 text-xs">Statut auto-post</p>
                    <p className="font-semibold text-lg">
                      {scrapingStatus?.settings?.facebook_auto_post_enabled ? '✅ Activée' : '⏸️ Désactivée'}
                    </p>
                  </div>
                </div>

                {/* Token Monitoring Widget */}
                <div className="mt-6 pt-6 border-t border-slate-200" data-testid="fb-token-monitoring">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4 text-slate-600" />
                      <h4 className="font-semibold text-slate-800">Surveillance du Page Access Token</h4>
                    </div>
                    <Button
                      onClick={checkFacebookToken}
                      disabled={facebookTokenChecking}
                      variant="ghost"
                      size="sm"
                      data-testid="fb-token-check-button"
                    >
                      {facebookTokenChecking ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Vérification...</>
                      ) : (
                        <><RefreshCw className="h-4 w-4 mr-2" /> Vérifier le token</>
                      )}
                    </Button>
                  </div>

                  {!facebookToken ? (
                    <div className="p-4 rounded-md bg-slate-50 text-sm text-slate-500">
                      Cliquez sur "Vérifier le token" pour interroger Facebook /debug_token
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Validity */}
                      <div className={`p-3 rounded-lg border ${facebookToken.is_valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <p className="text-xs text-slate-500">Validité</p>
                        <p className={`font-semibold ${facebookToken.is_valid ? 'text-green-700' : 'text-red-700'}`} data-testid="fb-token-valid">
                          {facebookToken.is_valid ? '✅ Token valide' : '❌ Token invalide'}
                        </p>
                        {facebookToken.error && (
                          <p className="text-xs text-red-600 mt-1">{facebookToken.error}</p>
                        )}
                      </div>

                      {/* Expiration */}
                      <div className={`p-3 rounded-lg border ${
                        facebookToken.is_permanent ? 'bg-green-50 border-green-200' :
                        facebookToken.days_remaining !== null && facebookToken.days_remaining < 7 ? 'bg-red-50 border-red-200' :
                        facebookToken.days_remaining !== null && facebookToken.days_remaining < 30 ? 'bg-amber-50 border-amber-200' :
                        'bg-slate-50 border-slate-200'
                      }`}>
                        <p className="text-xs text-slate-500">Expiration</p>
                        <p className="font-semibold" data-testid="fb-token-expires">
                          {facebookToken.is_permanent ? (
                            <span className="text-green-700">♾️ Permanent (n'expire jamais)</span>
                          ) : facebookToken.days_remaining !== null ? (
                            <span className={facebookToken.days_remaining < 7 ? 'text-red-700' : facebookToken.days_remaining < 30 ? 'text-amber-700' : 'text-slate-700'}>
                              {facebookToken.days_remaining} jours restants
                            </span>
                          ) : (
                            <span className="text-slate-500">N/A</span>
                          )}
                        </p>
                        {facebookToken.expires_at_iso && (
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(facebookToken.expires_at_iso).toLocaleString('fr-FR')}
                          </p>
                        )}
                      </div>

                      {/* Type & App */}
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <p className="text-xs text-slate-500">Type / Application</p>
                        <p className="font-semibold text-slate-800">
                          {facebookToken.type || 'N/A'}
                          {facebookToken.application && <span className="font-normal text-slate-500"> · {facebookToken.application}</span>}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">App ID: {facebookToken.app_id || '—'}</p>
                      </div>

                      {/* Page ID */}
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <p className="text-xs text-slate-500">Profile / Page ID</p>
                        <p className="font-mono text-sm text-slate-800">{facebookToken.profile_id || '—'}</p>
                        {facebookToken.issued_at && (
                          <p className="text-xs text-slate-500 mt-1">
                            Émis le {new Date(facebookToken.issued_at * 1000).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>

                      {/* Scopes */}
                      <div className="md:col-span-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <p className="text-xs text-slate-500 mb-2">Permissions accordées</p>
                        <div className="flex flex-wrap gap-1.5" data-testid="fb-token-scopes">
                          {(facebookToken.scopes || []).map((s) => {
                            const required = ['pages_manage_posts', 'pages_read_engagement'];
                            const isReq = required.includes(s);
                            return (
                              <Badge
                                key={s}
                                className={isReq ? 'bg-green-100 text-green-700 border-green-200' : 'bg-slate-100 text-slate-600'}
                              >
                                {isReq && '✓ '}{s}
                              </Badge>
                            );
                          })}
                          {(!facebookToken.scopes || facebookToken.scopes.length === 0) && (
                            <span className="text-xs text-slate-400">Aucune permission</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Info Box */}
              <Card className="p-4 bg-sky-50 border-sky-200">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-sky-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sky-900">Mode de fonctionnement</h4>
                    <p className="text-sm text-sky-700 mt-1">
                      {scrapingStatus?.demo_mode ? (
                        <>
                          <strong>Mode Démonstration actif:</strong> Des emplois réalistes sont générés automatiquement 
                          avec des entreprises de RDC (Vodacom, Rawbank, etc.). Pour activer le vrai scraping 
                          via Google Jobs, ajoutez la clé <code className="bg-sky-100 px-1 rounded">SERPAPI_KEY</code> dans le fichier .env.
                        </>
                      ) : (
                        <>
                          <strong>Mode Production actif:</strong> Les emplois sont scrapés depuis Google Jobs via SerpAPI 
                          avec le mot-clé "<strong>{scrapingStatus?.settings?.search_query || 'emploi RDC'}</strong>", 
                          filtre: <strong>{scrapingStatus?.settings?.date_posted === 'today' ? 'Dernières 24h' : 
                                          scrapingStatus?.settings?.date_posted === '3days' ? '3 derniers jours' : 
                                          scrapingStatus?.settings?.date_posted === 'week' ? 'Dernière semaine' : 
                                          scrapingStatus?.settings?.date_posted === 'month' ? 'Dernier mois' : 'Dernières 24h'}</strong>
                          (max {scrapingStatus?.settings?.num_results || 100} résultats).
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users" className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Gestion des Utilisateurs</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    {users.length} utilisateur(s) au total
                  </p>
                </div>
                <Button
                  onClick={() => setCreateUserDialogOpen(true)}
                  className="bg-sky-500 hover:bg-sky-600"
                  data-testid="create-user-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvel utilisateur
                </Button>
              </div>

              {/* Filtres Utilisateurs */}
              <div className="bg-slate-50 border rounded-lg p-4">
                <div className="flex flex-wrap items-end gap-4">
                  {/* Recherche */}
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs text-slate-500 mb-1 block">Recherche</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Nom ou email..."
                        value={searchUser}
                        onChange={(e) => setSearchUser(e.target.value)}
                        className="pl-10"
                        data-testid="search-users-input"
                      />
                    </div>
                  </div>
                  
                  {/* Filtre Statut */}
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">Statut</Label>
                    <Select 
                      value={usersFilter.status}
                      onValueChange={(value) => setUsersFilter({...usersFilter, status: value})}
                    >
                      <SelectTrigger data-testid="filter-users-status">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                        <SelectItem value="free">Gratuit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Date Du */}
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">Inscrit du</Label>
                    <Input
                      type="date"
                      value={usersFilter.dateFrom}
                      onChange={(e) => setUsersFilter({...usersFilter, dateFrom: e.target.value})}
                      data-testid="filter-users-date-from"
                    />
                  </div>
                  
                  {/* Date Au */}
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">Inscrit au</Label>
                    <Input
                      type="date"
                      value={usersFilter.dateTo}
                      onChange={(e) => setUsersFilter({...usersFilter, dateTo: e.target.value})}
                      data-testid="filter-users-date-to"
                    />
                  </div>
                  
                  {/* Nombre */}
                  <div className="w-32">
                    <Label className="text-xs text-slate-500 mb-1 block">Afficher</Label>
                    <Select 
                      value={String(usersFilter.limit)}
                      onValueChange={(value) => setUsersFilter({...usersFilter, limit: parseInt(value)})}
                    >
                      <SelectTrigger data-testid="filter-users-limit">
                        <SelectValue placeholder="50" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="500">Tous</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Bouton Reset */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setUsersFilter({ status: 'all', dateFrom: '', dateTo: '', limit: 50 });
                      setSearchUser('');
                      setSelectedUsers([]);
                      setSelectAllUsers(false);
                    }}
                    data-testid="reset-users-filters"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Réinitialiser
                  </Button>
                  
                  {/* Bouton Export CSV */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportUsersToCSV}
                    data-testid="export-users-csv"
                    className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Exporter CSV
                  </Button>
                </div>
                
                {/* Actions en masse */}
                {selectedUsers.length > 0 && (
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t">
                    <span className="text-sm text-slate-600">
                      <CheckSquare className="h-4 w-4 inline mr-1" />
                      {selectedUsers.length} utilisateur(s) sélectionné(s)
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedUsers([]); setSelectAllUsers(false); }}
                    >
                      Désélectionner tout
                    </Button>
                  </div>
                )}
              </div>

              <div className="border rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={selectAllUsers}
                          onChange={() => handleSelectAllUsers(filteredUsers.slice(0, usersFilter.limit))}
                          className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                          data-testid="select-all-users"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Nom</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Catégorie</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Expiration</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Préférences</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredUsers
                      .filter(user => {
                        const matchesStatus = 
                          usersFilter.status === 'all' ||
                          (usersFilter.status === 'premium' && user.is_premium) ||
                          (usersFilter.status === 'free' && !user.is_premium);
                        
                        let matchesDate = true;
                        if (user.created_at) {
                          const userDate = new Date(user.created_at);
                          if (usersFilter.dateFrom) {
                            matchesDate = matchesDate && userDate >= new Date(usersFilter.dateFrom);
                          }
                          if (usersFilter.dateTo) {
                            matchesDate = matchesDate && userDate <= new Date(usersFilter.dateTo + 'T23:59:59');
                          }
                        }
                        return matchesStatus && matchesDate;
                      })
                      .slice(0, usersFilter.limit)
                      .map((user) => (
                      <tr key={user.id} className={`hover:bg-slate-50 ${selectedUsers.includes(user.id) ? 'bg-sky-50' : ''}`}>
                        <td className="px-2 py-3">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => handleUserSelect(user.id)}
                            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <span>{user.nom}</span>
                            {user.is_active === false && (
                              <Badge className="bg-red-100 text-red-700 border-red-200 text-xs" data-testid={`user-disabled-${user.id}`}>
                                Désactivé
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{user.email}</td>
                        <td className="px-4 py-3">
                          <Badge 
                            style={{ backgroundColor: user.category_color || '#6B7280' }}
                            className="text-white"
                          >
                            {getCategoryIcon(user.category_id)}
                            <span className="ml-1">{user.category_name || 'Free'}</span>
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {user.category_id !== 'free' && user.category_expires ? (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4 text-slate-400" />
                              {user.category_expired ? (
                                <span className="text-red-600 font-medium">Expiré</span>
                              ) : (
                                <span className={user.days_remaining <= 7 ? 'text-orange-600' : 'text-slate-600'}>
                                  {user.days_remaining} jours
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {user.preferences?.keywords?.length || 0} mots-clés,{' '}
                          {user.preferences?.locations?.length || 0} lieux
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={user.is_premium ? "outline" : "default"}
                              onClick={() => toggleUserPremium(user.id, user.is_premium)}
                              className={user.is_premium ? "" : "bg-yellow-500 hover:bg-yellow-600"}
                            >
                              {user.is_premium ? 'Retirer Premium' : 'Activer Premium'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPasswordDialog(user)}
                              title="Changer le mot de passe"
                              data-testid={`change-password-${user.id}`}
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleUserActive(user.id, user.is_active !== false)}
                              title={user.is_active === false ? 'Réactiver le compte' : 'Désactiver le compte'}
                              className={user.is_active === false ? 'text-green-600 border-green-200 hover:bg-green-50' : 'text-red-600 border-red-200 hover:bg-red-50'}
                              data-testid={`toggle-active-${user.id}`}
                            >
                              {user.is_active === false ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Categories Tab */}
            <TabsContent value="categories" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Gestion des Catégories d'abonnement</h2>
                <Button 
                  onClick={() => { resetCategoryForm(); setCategoryDialogOpen(true); }}
                  className="bg-sky-500 hover:bg-sky-600"
                  data-testid="add-category-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle catégorie
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {categories.map((category) => (
                  <Card 
                    key={category.id} 
                    className={`p-4 ${!category.is_active ? 'opacity-60' : ''}`}
                    data-testid={`category-card-${category.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: category.color }}
                      >
                        {getCategoryIcon(category.id)}
                      </div>
                      {category.is_default && (
                        <Badge variant="outline" className="text-xs">Défaut</Badge>
                      )}
                    </div>
                    
                    <h3 className="font-semibold text-lg">{category.name}</h3>
                    <p className="text-sm text-slate-500">{category.name_fr}</p>
                    
                    <div className="mt-3 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Prix:</span>
                        <span className="font-semibold">
                          {category.cost > 0 ? `$${category.cost}` : 'Gratuit'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Validité:</span>
                        <span>{category.validity_days > 0 ? `${category.validity_days} jours` : 'Illimitée'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Notifications:</span>
                        <span>
                          {category.services?.max_notifications_monthly === -1 
                            ? 'Illimité' 
                            : category.services?.max_notifications_monthly}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => openEditCategory(category)}
                        disabled={category.is_default}
                        data-testid={`edit-category-${category.id}`}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Modifier
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        onClick={() => handleDeleteCategory(category.id)}
                        disabled={category.is_default}
                        data-testid={`delete-category-${category.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold mb-2">Services par catégorie</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="px-3 py-2 text-left">Catégorie</th>
                        <th className="px-3 py-2 text-center">Web</th>
                        <th className="px-3 py-2 text-center">Email</th>
                        <th className="px-3 py-2 text-center">SMS</th>
                        <th className="px-3 py-2 text-center">WhatsApp</th>
                        <th className="px-3 py-2 text-center">CV Upload</th>
                        <th className="px-3 py-2 text-center">Support</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((cat) => (
                        <tr key={cat.id} className="border-b">
                          <td className="px-3 py-2 font-medium">{cat.name}</td>
                          <td className="px-3 py-2 text-center">
                            {cat.services?.notification_channels?.includes('web') 
                              ? <Check className="h-4 w-4 text-green-500 mx-auto" /> 
                              : <X className="h-4 w-4 text-slate-300 mx-auto" />}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {cat.services?.notification_channels?.includes('email') 
                              ? <Check className="h-4 w-4 text-green-500 mx-auto" /> 
                              : <X className="h-4 w-4 text-slate-300 mx-auto" />}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {cat.services?.notification_channels?.includes('sms') 
                              ? <Check className="h-4 w-4 text-green-500 mx-auto" /> 
                              : <X className="h-4 w-4 text-slate-300 mx-auto" />}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {cat.services?.notification_channels?.includes('whatsapp') 
                              ? <Check className="h-4 w-4 text-green-500 mx-auto" /> 
                              : <X className="h-4 w-4 text-slate-300 mx-auto" />}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {cat.services?.cv_upload 
                              ? <Check className="h-4 w-4 text-green-500 mx-auto" /> 
                              : <X className="h-4 w-4 text-slate-300 mx-auto" />}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {cat.services?.priority_support 
                              ? <Check className="h-4 w-4 text-green-500 mx-auto" /> 
                              : <X className="h-4 w-4 text-slate-300 mx-auto" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Jobs Management Tab */}
            <TabsContent value="jobs" className="space-y-6">
              {/* Auto-Archive Scheduler Control */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${archiveSchedulerStatus?.is_running ? 'bg-green-100' : 'bg-slate-100'}`}>
                      <Archive className={`h-5 w-5 ${archiveSchedulerStatus?.is_running ? 'text-green-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">Auto-archivage automatique</h3>
                      <p className="text-sm text-slate-600">
                        {archiveSchedulerStatus?.is_running 
                          ? `Actif - Archive les emplois de plus de ${archiveSchedulerStatus?.archive_days || 60} jours (toutes les ${archiveSchedulerStatus?.interval_hours || 24}h)`
                          : 'Désactivé - Les emplois anciens ne seront pas archivés automatiquement'}
                      </p>
                      {archiveSchedulerStatus?.last_run && (
                        <p className="text-xs text-slate-500 mt-1">
                          Dernière exécution: {new Date(archiveSchedulerStatus.last_run).toLocaleString('fr-FR')}
                          {archiveSchedulerStatus?.total_archived > 0 && ` • ${archiveSchedulerStatus.total_archived} emplois archivés au total`}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={archiveSchedulerStatus?.is_running ? "destructive" : "default"}
                      size="sm"
                      onClick={() => toggleArchiveScheduler(!archiveSchedulerStatus?.is_running)}
                      data-testid="toggle-archive-scheduler"
                    >
                      {archiveSchedulerStatus?.is_running ? (
                        <>
                          <Pause className="h-4 w-4 mr-1" />
                          Désactiver
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-1" />
                          Activer
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Gestion des Offres d'emploi</h2>
                  {jobsStats && (
                    <p className="text-sm text-slate-500 mt-1">
                      {jobsStats.active} actifs, {jobsStats.archived} archivés 
                      {jobsStats.auto_archived > 0 && <span className="text-orange-500"> ({jobsStats.auto_archived} auto-archivés)</span>}
                      {' '}sur {jobsStats.total} total
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await axios.post(`${API}/admin/jobs/auto-archive?days=60`, {}, { headers: getHeaders() });
                        toast.success(response.data.message);
                        fetchJobs();
                        fetchJobsStats();
                        fetchArchiveSchedulerStatus();
                      } catch (error) {
                        toast.error('Erreur lors de l\'auto-archivage');
                      }
                    }}
                    title="Archiver manuellement les emplois de plus de 2 mois"
                    data-testid="auto-archive-button"
                  >
                    <Archive className="h-4 w-4 mr-1" />
                    Archiver maintenant (&gt;60j)
                  </Button>
                </div>
              </div>

              {/* Filtres */}
              <div className="bg-slate-50 border rounded-lg p-4">
                <div className="flex flex-wrap items-end gap-4">
                  {/* Recherche */}
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs text-slate-500 mb-1 block">Recherche</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Titre ou entreprise..."
                        value={searchJob}
                        onChange={(e) => setSearchJob(e.target.value)}
                        className="pl-10"
                        data-testid="search-jobs-input"
                      />
                    </div>
                  </div>
                  
                  {/* Filtre Statut */}
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">Statut</Label>
                    <Select 
                      value={jobsFilter.status}
                      onValueChange={(value) => setJobsFilter({...jobsFilter, status: value})}
                    >
                      <SelectTrigger data-testid="filter-status">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="active">Actifs</SelectItem>
                        <SelectItem value="archived">Archivés</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Date Du */}
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">Date du</Label>
                    <Input
                      type="date"
                      value={jobsFilter.dateFrom}
                      onChange={(e) => setJobsFilter({...jobsFilter, dateFrom: e.target.value})}
                      data-testid="filter-date-from"
                    />
                  </div>
                  
                  {/* Date Au */}
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">Date au</Label>
                    <Input
                      type="date"
                      value={jobsFilter.dateTo}
                      onChange={(e) => setJobsFilter({...jobsFilter, dateTo: e.target.value})}
                      data-testid="filter-date-to"
                    />
                  </div>
                  
                  {/* Nombre d'emplois */}
                  <div className="w-32">
                    <Label className="text-xs text-slate-500 mb-1 block">Afficher</Label>
                    <Select 
                      value={String(jobsFilter.limit)}
                      onValueChange={(value) => setJobsFilter({...jobsFilter, limit: parseInt(value)})}
                    >
                      <SelectTrigger data-testid="filter-limit">
                        <SelectValue placeholder="50" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                        <SelectItem value="500">Tous (500)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Bouton Reset */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setJobsFilter({ status: 'all', dateFrom: '', dateTo: '', limit: 50 });
                      setSearchJob('');
                      setSelectedJobs([]);
                      setSelectAllJobs(false);
                    }}
                    data-testid="reset-filters"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Réinitialiser
                  </Button>
                  
                  {/* Bouton Export CSV */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportJobsToCSV}
                    data-testid="export-csv"
                    className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Exporter CSV
                  </Button>
                </div>
                
                {/* Actions en masse */}
                {selectedJobs.length > 0 && (
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t">
                    <span className="text-sm text-slate-600">
                      <CheckSquare className="h-4 w-4 inline mr-1" />
                      {selectedJobs.length} emploi(s) sélectionné(s)
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBulkArchiveJobs}
                      className="text-orange-600 border-orange-200 hover:bg-orange-50"
                    >
                      <Archive className="h-4 w-4 mr-1" />
                      Archiver
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDeleteJobs}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Supprimer
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedJobs([]); setSelectAllJobs(false); }}
                    >
                      Désélectionner tout
                    </Button>
                  </div>
                )}
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={selectAllJobs}
                          onChange={() => {
                            const filteredJobs = jobs.filter(job => {
                              const matchesSearch = job.titre?.toLowerCase().includes(searchJob.toLowerCase()) || job.entreprise?.toLowerCase().includes(searchJob.toLowerCase());
                              const matchesStatus = jobsFilter.status === 'all' || (jobsFilter.status === 'active' && !job.archived) || (jobsFilter.status === 'archived' && job.archived);
                              return matchesSearch && matchesStatus;
                            }).slice(0, jobsFilter.limit);
                            handleSelectAllJobs(filteredJobs);
                          }}
                          className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                          data-testid="select-all-jobs"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Titre</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Entreprise</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Localisation</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Source</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Statut</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {jobs
                      .filter(job => {
                        // Filtre par recherche texte
                        const matchesSearch = 
                          job.titre?.toLowerCase().includes(searchJob.toLowerCase()) ||
                          job.entreprise?.toLowerCase().includes(searchJob.toLowerCase());
                        
                        // Filtre par statut
                        const matchesStatus = 
                          jobsFilter.status === 'all' ||
                          (jobsFilter.status === 'active' && !job.archived) ||
                          (jobsFilter.status === 'archived' && job.archived);
                        
                        // Filtre par date
                        let matchesDate = true;
                        if (job.date_publication) {
                          const jobDate = new Date(job.date_publication);
                          if (jobsFilter.dateFrom) {
                            matchesDate = matchesDate && jobDate >= new Date(jobsFilter.dateFrom);
                          }
                          if (jobsFilter.dateTo) {
                            matchesDate = matchesDate && jobDate <= new Date(jobsFilter.dateTo + 'T23:59:59');
                          }
                        }
                        
                        return matchesSearch && matchesStatus && matchesDate;
                      })
                      .slice(0, jobsFilter.limit)
                      .map((job) => (
                      <tr key={job.id} className={`hover:bg-slate-50 ${job.archived ? 'bg-slate-100' : ''} ${selectedJobs.includes(job.id) ? 'bg-sky-50' : ''}`}>
                        <td className="px-2 py-3">
                          <input
                            type="checkbox"
                            checked={selectedJobs.includes(job.id)}
                            onChange={() => handleJobSelect(job.id)}
                            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                            data-testid={`select-job-${job.id}`}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium max-w-xs truncate">
                          {job.titre}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{job.entreprise}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{job.localisation}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-testid={`admin-job-date-${job.id}`}>
                          {formatPublicationDate(job)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="outline">{job.source}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          {job.archived ? (
                            <Badge className="bg-slate-200 text-slate-700">
                              <Archive className="h-3 w-3 mr-1" />
                              Archivé
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">
                              <Eye className="h-3 w-3 mr-1" />
                              Actif
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {job.archived ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUnarchiveJob(job.id)}
                                data-testid={`unarchive-job-${job.id}`}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                Restaurer
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleArchiveJob(job.id)}
                                data-testid={`archive-job-${job.id}`}
                              >
                                <Archive className="h-3 w-3 mr-1" />
                                Archiver
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteJob(job.id)}
                              data-testid={`delete-job-${job.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {jobs.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Briefcase className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucun emploi trouvé</p>
                </div>
              )}
            </TabsContent>

            {/* Companies Tab */}
            <TabsContent value="companies" className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Gestion des Entreprises</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchCompany}
                    onChange={(e) => setSearchCompany(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Entreprise</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Secteur</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Statut</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredCompanies.map((company) => (
                      <tr key={company.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium">{company.nom}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{company.email}</td>
                        <td className="px-4 py-3 text-sm">{company.secteur}</td>
                        <td className="px-4 py-3">
                          {company.verified ? (
                            <Badge className="bg-green-100 text-green-800">
                              <Check className="h-3 w-3 mr-1" />
                              Vérifiée
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              <X className="h-3 w-3 mr-1" />
                              Non vérifiée
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            size="sm"
                            variant={company.verified ? "outline" : "default"}
                            onClick={() => toggleCompanyVerification(company.id, company.verified)}
                            className={company.verified ? "" : "bg-green-500 hover:bg-green-600"}
                          >
                            {company.verified ? 'Retirer vérification' : 'Vérifier'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-6">
              <h2 className="text-2xl font-bold">Analytics & Statistiques</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Jobs by Domain */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <PieChart className="h-5 w-5 mr-2 text-blue-500" />
                    Emplois par Domaine
                  </h3>
                  <div className="space-y-3">
                    {analytics?.jobs_by_domain?.slice(0, 8).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full bg-blue-${(idx + 3) * 100}`}></div>
                          <span className="text-sm">{item.domain}</span>
                        </div>
                        <Badge variant="outline">{item.count}</Badge>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Jobs by Location */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2 text-green-500" />
                    Emplois par Localisation
                  </h3>
                  <div className="space-y-3">
                    {analytics?.jobs_by_location?.slice(0, 8).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full bg-green-${(idx + 3) * 100}`}></div>
                          <span className="text-sm">{item.location}</span>
                        </div>
                        <Badge variant="outline">{item.count}</Badge>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Companies by Sector */}
                <Card className="p-6 md:col-span-2">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <Building2 className="h-5 w-5 mr-2 text-purple-500" />
                    Entreprises par Secteur
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {analytics?.companies_by_sector?.map((item, idx) => (
                      <div key={idx} className="p-3 bg-purple-50 rounded-lg">
                        <p className="text-sm text-slate-600">{item.sector}</p>
                        <p className="text-2xl font-bold text-purple-600">{item.count}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* Push Notification Tab */}
            <TabsContent value="push" className="space-y-4">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-2xl font-bold mb-6">Envoyer une Notification Push</h2>
                
                <Card className="p-6">
                  <form onSubmit={sendPushNotification} className="space-y-4">
                    <div>
                      <Label>Titre de la notification</Label>
                      <Input
                        value={pushForm.title}
                        onChange={(e) => setPushForm({ ...pushForm, title: e.target.value })}
                        required
                        placeholder="Ex: Nouvelle mise à jour"
                      />
                    </div>

                    <div>
                      <Label>Message</Label>
                      <Textarea
                        value={pushForm.message}
                        onChange={(e) => setPushForm({ ...pushForm, message: e.target.value })}
                        required
                        rows={4}
                        placeholder="Votre message..."
                      />
                    </div>

                    <div>
                      <Label>Cible</Label>
                      <Select value={pushForm.target} onValueChange={(value) => setPushForm({ ...pushForm, target: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les utilisateurs</SelectItem>
                          <SelectItem value="premium">Utilisateurs Premium uniquement</SelectItem>
                          <SelectItem value="free">Utilisateurs Gratuits uniquement</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {loading ? 'Envoi...' : 'Envoyer la notification'}
                    </Button>
                  </form>
                </Card>

                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Les notifications seront envoyées en temps réel à tous les utilisateurs 
                    correspondant au segment sélectionné et apparaîtront dans leur dashboard.
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* Category Edit/Create Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </DialogTitle>
            <DialogDescription>
              Configurez les paramètres de la catégorie d'abonnement
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cat-name">Nom (EN)</Label>
                <Input
                  id="cat-name"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="Gold"
                  className="mt-1"
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <Label htmlFor="cat-name-fr">Nom (FR)</Label>
                <Input
                  id="cat-name-fr"
                  value={categoryForm.name_fr}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name_fr: e.target.value })}
                  placeholder="Or"
                  className="mt-1"
                  data-testid="category-name-fr-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cat-cost">Prix (USD)</Label>
                <Input
                  id="cat-cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={categoryForm.cost}
                  onChange={(e) => setCategoryForm({ ...categoryForm, cost: parseFloat(e.target.value) })}
                  className="mt-1"
                  data-testid="category-cost-input"
                />
              </div>
              <div>
                <Label htmlFor="cat-validity">Validité (jours)</Label>
                <Input
                  id="cat-validity"
                  type="number"
                  min="0"
                  value={categoryForm.validity_days}
                  onChange={(e) => setCategoryForm({ ...categoryForm, validity_days: parseInt(e.target.value) })}
                  className="mt-1"
                  data-testid="category-validity-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="cat-color">Couleur</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="cat-color"
                  type="color"
                  value={categoryForm.color}
                  onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                  className="w-16 h-10 p-1"
                  data-testid="category-color-input"
                />
                <Input
                  type="text"
                  value={categoryForm.color}
                  onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                  className="flex-1"
                  placeholder="#F59E0B"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setCategoryDialogOpen(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleSaveCategory}
                className="flex-1 bg-sky-500 hover:bg-sky-600"
                data-testid="save-category-button"
              >
                {editingCategory ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Section Edit Dialog */}
      <Dialog open={sectionDialogOpen} onOpenChange={setSectionDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Éditer la section: {editingSection?.name}
            </DialogTitle>
            <DialogDescription>
              Modifiez le contenu de cette section
            </DialogDescription>
          </DialogHeader>
          
          {editingSection && (
            <div className="space-y-4">
              {editingSection.content?.title !== undefined && (
                <div>
                  <Label>Titre</Label>
                  <Input
                    value={editingSection.content.title || ''}
                    onChange={(e) => setEditingSection({
                      ...editingSection,
                      content: { ...editingSection.content, title: e.target.value }
                    })}
                    className="mt-1"
                  />
                </div>
              )}
              
              {editingSection.content?.subtitle !== undefined && (
                <div>
                  <Label>Sous-titre / Description</Label>
                  <Textarea
                    value={editingSection.content.subtitle || ''}
                    onChange={(e) => setEditingSection({
                      ...editingSection,
                      content: { ...editingSection.content, subtitle: e.target.value }
                    })}
                    className="mt-1"
                    rows={3}
                  />
                </div>
              )}
              
              {editingSection.content?.button_text !== undefined && (
                <div>
                  <Label>Texte du bouton</Label>
                  <Input
                    value={editingSection.content.button_text || ''}
                    onChange={(e) => setEditingSection({
                      ...editingSection,
                      content: { ...editingSection.content, button_text: e.target.value }
                    })}
                    className="mt-1"
                  />
                </div>
              )}
              
              {editingSection.content?.search_placeholder !== undefined && (
                <div>
                  <Label>Placeholder de recherche</Label>
                  <Input
                    value={editingSection.content.search_placeholder || ''}
                    onChange={(e) => setEditingSection({
                      ...editingSection,
                      content: { ...editingSection.content, search_placeholder: e.target.value }
                    })}
                    className="mt-1"
                  />
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setSectionDialogOpen(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button 
                  onClick={() => handleUpdateSectionContent(editingSection.id, editingSection.content)}
                  className="flex-1 bg-sky-500 hover:bg-sky-600"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-sky-600" />
              Créer un nouvel utilisateur
            </DialogTitle>
            <DialogDescription>
              Cet utilisateur pourra immédiatement se connecter avec ces identifiants.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div>
              <Label htmlFor="new-user-nom">Nom complet *</Label>
              <Input
                id="new-user-nom"
                value={newUserForm.nom}
                onChange={(e) => setNewUserForm({...newUserForm, nom: e.target.value})}
                placeholder="Jean Mukendi"
                className="mt-1"
                data-testid="new-user-nom"
              />
            </div>
            <div>
              <Label htmlFor="new-user-email">Email *</Label>
              <Input
                id="new-user-email"
                type="email"
                value={newUserForm.email}
                onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                placeholder="user@example.com"
                className="mt-1"
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label htmlFor="new-user-password">Mot de passe *</Label>
              <Input
                id="new-user-password"
                type="text"
                value={newUserForm.password}
                onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})}
                placeholder="Minimum 6 caractères"
                className="mt-1"
                data-testid="new-user-password"
              />
            </div>
            <div>
              <Label htmlFor="new-user-phone">Téléphone</Label>
              <Input
                id="new-user-phone"
                value={newUserForm.phone}
                onChange={(e) => setNewUserForm({...newUserForm, phone: e.target.value})}
                placeholder="+243..."
                className="mt-1"
                data-testid="new-user-phone"
              />
            </div>
            <div>
              <Label htmlFor="new-user-category">Catégorie initiale</Label>
              <Select
                value={newUserForm.category_id}
                onValueChange={(value) => setNewUserForm({...newUserForm, category_id: value})}
              >
                <SelectTrigger id="new-user-category" className="mt-1" data-testid="new-user-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name_fr || cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setCreateUserDialogOpen(false)}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={creatingUser || !newUserForm.nom || !newUserForm.email || !newUserForm.password || newUserForm.password.length < 6}
              className="flex-1 bg-sky-500 hover:bg-sky-600"
              data-testid="confirm-create-user"
            >
              {creatingUser ? 'Création...' : 'Créer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-sky-600" />
              Changer le mot de passe
            </DialogTitle>
            <DialogDescription>
              {selectedUserForPassword && (
                <>Modifier le mot de passe de <strong>{selectedUserForPassword.email}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-password">Nouveau mot de passe</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 caractères"
                className="mt-1"
                data-testid="new-password-input"
              />
            </div>
            <p className="text-xs text-slate-500">
              L'utilisateur devra utiliser ce nouveau mot de passe pour se connecter.
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setPasswordDialogOpen(false);
                setNewPassword('');
                setSelectedUserForPassword(null);
              }}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              onClick={handleChangeUserPassword}
              disabled={passwordLoading || !newPassword || newPassword.length < 6}
              className="flex-1 bg-sky-500 hover:bg-sky-600"
              data-testid="confirm-password-change"
            >
              {passwordLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Modification...
                </>
              ) : (
                'Confirmer'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
